import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const coveredChapters = [
  "General: scope, codes, standards, abbreviations and definitions",
  "Means of escape, travel distances, exit capacity and staircases",
  "Structural fire precautions, compartmentation and material controls",
  "Site planning, fire engine access and external firefighting provision",
  "Electrical power supplies for fire safety systems",
  "Firefighting systems: hydrants, hose reels, sprinklers and rising mains",
  "Mechanical ventilation and smoke control systems",
  "Emergency lighting, exit signs and voice communication systems",
  "Additional requirements for each purpose group",
  "Special installations including solar PV and atriums",
  "Regulated fire safety products and materials",
];

const benefits = [
  "Critical decision points are surfaced as explicit questions instead of being buried in cross-references.",
  "Every outcome is anchored to clause references so the compliance path can be checked and discussed with a QP or fire safety professional.",
  "Thresholds and conditions are shown in the workflow so project teams can see which obligation applies before submission.",
  "Shareable result links help consultants, contractors and owners review the same compliance logic.",
  "The same clause logic can be applied consistently across a portfolio of Singapore projects.",
];

export default function ScdfFireCodeWorkflowsArticle() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Introducing SCDF Fire Code 2023 Digital Workflows",
            description:
              "FireSafetySingapore.com offers interactive decision-tree workflows for SCDF Fire Code 2023 compliance navigation in Singapore.",
            author: { "@type": "Organization", name: "FireSafetySingapore.com" },
            publisher: { "@type": "Organization", name: "Ezzogenics" },
            datePublished: "2026-06-24",
            mainEntityOfPage: "https://firesafetysingapore.com/resources/scdf-fire-code-2023-digital-workflows",
          }),
        }}
      />
      <main>
        <section className="relative pt-20 min-h-[420px] md:min-h-[520px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/7ec9b5c3-3aee-4b42-bca0-cd81a242fd02_compressed_Depositphotos_25184919_l-2015.webp"
              alt="SCDF Fire Code 2023 Digital Workflows"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/55 to-foreground-950/25"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">SCDF Fire Code 2023 Workflows</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Beta Launch
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-4xl">
              The Fire Code 2023, Reimagined as Decision Trees You Can Actually Follow
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-3xl leading-relaxed">
              FireSafetySingapore.com now hosts interactive digital workflows covering all 11 chapters of the SCDF Fire Code 2023, turning dense clauses into clear, clause-referenced compliance paths.
            </p>
          </div>
        </section>

        <article className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-8">
            <span>By FireSafetySingapore.com</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <time dateTime="2026-06-24">24 June 2026</time>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>SCDF Fire Code 2023</span>
          </div>

          <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-600 prose-li:text-foreground-600">
            <p>
              Anyone who has opened the SCDF Fire Code 2023 knows the problem: hundreds of pages, cross-referenced clauses, conditional thresholds, purpose-group exceptions, and a single missed sub-clause that can stall a submission or trigger redesign.
            </p>
            <p>
              The code is comprehensive, but that comprehensiveness is exactly what makes it hard to apply on a live project. The new FireSafetySingapore.com workflow viewer breaks the Fire Code into click-through decision trees so project teams can move from a practical question to a clause-referenced compliance result.
            </p>

            <h2>What's Covered</h2>
            <p>
              The beta viewer maps all 11 chapters of the SCDF Fire Code 2023 into structured workflows, searchable clauses and shareable outcomes.
            </p>
            <ol>
              {coveredChapters.map((chapter) => (
                <li key={chapter}>{chapter}</li>
              ))}
            </ol>

            <h2>Why This Reduces Compliance Risk</h2>
            <p>
              For contractors and specialty-service operators, the risk is not usually that the code is unknowable. The risk is that the relevant clause is easy to miss, easy to misread, or buried several references deep when a project is moving quickly.
            </p>
            <ul>
              {benefits.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>

            <h2>Try the Beta Viewer</h2>
            <p>
              The workflow viewer is live in beta. Browse by chapter, jump directly to a clause, work through a decision tree, and share the result link with your team or consultants.
            </p>

            <blockquote>
              These workflows are an interpretive navigation aid for the SCDF Fire Code 2023. They help teams find and apply clauses faster, but they do not replace the official code, current SCDF circulars, or the judgement of a Qualified Person or registered fire safety professional.
            </blockquote>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/fire-code"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Open the SCDF Fire Code Workflows
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/resources"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              Back to Resources
            </Link>
          </div>

          <p className="text-xs text-foreground-500 leading-relaxed mt-10">
            Source: Workflows derived from the Singapore Civil Defence Force Code of Practice for Fire Precautions in Buildings 2023. Always verify against the official code and current SCDF circulars before submission.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}
